// @ts-nocheck
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag, Tooltip, Spin, Empty } from 'antd';
import { 
  DollarOutlined, 
  BankOutlined, 
  CreditCardOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';
import { CardContainer } from './CardContainer';
import { SectionHeader } from './SectionHeader';
import { TransactionDetailModal } from './TransactionDetailModal';
import { colors, spacing } from '../design-system/tokens';
import { transactionsApi } from '../../services/api';

// Dollar icon SVG (matching design system style)
const DollarIcon: React.FC = () => (
  <svg 
    width="15" 
    height="24" 
    viewBox="0 0 15 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M7.5 1V23M11.5 5H5.75C4.95435 5 4.19129 5.31607 3.62868 5.87868C3.06607 6.44129 2.75 7.20435 2.75 8C2.75 8.79565 3.06607 9.55871 3.62868 10.1213C4.19129 10.6839 4.95435 11 5.75 11H9.25C10.0457 11 10.8087 11.3161 11.3713 11.8787C11.9339 12.4413 12.25 13.2043 12.25 14C12.25 14.7957 11.9339 15.5587 11.3713 16.1213C10.8087 16.6839 10.0457 17 9.25 17H3.5" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Types
interface Transaction {
  id: string;
  transactionType: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE';
  direction: 'BUYER_RECEIVABLE' | 'SUPPLIER_PAYABLE' | 'COMMISSION';
  amount: number | string;
  date: string;
  dueDate?: string;
  paymentMethod?: 'WIRE' | 'CHECK' | 'ACH' | null;
  reference?: string;
  description?: string;
  status: 'PENDING' | 'PAID' | 'PARTIAL' | 'VOIDED';
  legacyTable?: string;
}

interface PartyProfit {
  netCommission: number;
  netMargin: number;
  netProfit: number;
}

interface PaymentSummary {
  shipmentId: string;
  shipmentRef: string;
  buyerInvoiced: number;
  buyerPaid: number;
  buyerOutstanding: number;
  supplierInvoiced: number;
  supplierPaid: number;
  supplierOutstanding: number;
  commissionAmount: number;
  commissionPaid: number;
  // Gross calculations
  grossMargin: number;
  grossCommission: number;
  grossProfit: number;
  // Profit sharing
  commissionShare: number;
  marginShare: number;
  rimtrade: PartyProfit;
  commwell: PartyProfit;
  status: 'UNPAID' | 'BUYER_PARTIAL' | 'BUYER_PAID' | 'SUPPLIER_PARTIAL' | 'FULLY_SETTLED';
  transactions: Transaction[];
}

interface PaymentTrackingCardProps {
  shipmentId: string;
  shipmentRef?: string;
  className?: string;
  onTransactionClick?: (transaction: Transaction) => void;
}

// Format currency - compact version
const formatCurrency = (amount: number | string): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return '$' + Math.abs(num).toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

// Format date - compact
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).toUpperCase();
};

// Get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'FULLY_SETTLED':
      return colors.status.positive;
    case 'BUYER_PAID':
    case 'SUPPLIER_PARTIAL':
    case 'BUYER_PARTIAL':
      return colors.status.warning || '#faad14';
    case 'UNPAID':
    default:
      return colors.status.negative;
  }
};

// Get status label
const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'FULLY_SETTLED': return 'Settled';
    case 'BUYER_PAID': return 'Buyer Paid';
    case 'SUPPLIER_PARTIAL': return 'Supplier Partial';
    case 'BUYER_PARTIAL': return 'Partial';
    case 'UNPAID': return 'Unpaid';
    default: return status;
  }
};

// Get payment method icon
const getPaymentMethodIcon = (method?: string | null) => {
  switch (method) {
    case 'WIRE': return <BankOutlined style={{ fontSize: 12 }} />;
    case 'CHECK': return <CreditCardOutlined style={{ fontSize: 12 }} />;
    case 'ACH': return <BankOutlined style={{ fontSize: 12 }} />;
    default: return <DollarOutlined style={{ fontSize: 12 }} />;
  }
};

// Transaction Row Component - Compact
const TransactionRow: React.FC<{
  transaction: Transaction;
  onClick?: () => void;
}> = ({ transaction, onClick }) => {
  const amount = typeof transaction.amount === 'string' 
    ? parseFloat(transaction.amount) 
    : transaction.amount;
  
  const isPayment = transaction.transactionType === 'PAYMENT';
  
  // Color logic:
  // - BUYER_RECEIVABLE payments = green (money coming in from buyer)
  // - COMMISSION payments = green (commission coming in from buyer)
  // - SUPPLIER_PAYABLE payments = red (money going out to supplier)
  // - Invoices/Credit Notes = neutral
  const getAmountColor = () => {
    if (!isPayment) return colors.text.primary;
    
    switch (transaction.direction) {
      case 'BUYER_RECEIVABLE':
      case 'COMMISSION':
        return colors.status.positive; // Money coming IN
      case 'SUPPLIER_PAYABLE':
        return colors.status.negative; // Money going OUT
      default:
        return colors.text.primary;
    }
  };
  
  return (
    <div 
      onClick={onClick}
      style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '6px 8px',
        backgroundColor: '#f9f9f9',
        borderRadius: '4px',
        marginBottom: '4px',
        cursor: onClick ? 'pointer' : 'default',
        fontSize: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ color: '#999' }}>
          {getPaymentMethodIcon(transaction.paymentMethod)}
        </span>
        <span style={{ fontWeight: 500 }}>
          {formatDate(transaction.date)}
        </span>
        {transaction.legacyTable && (
          <Tag style={{ fontSize: '9px', padding: '0 4px', lineHeight: '14px' }}>Legacy</Tag>
        )}
      </div>
      <div style={{ 
        fontWeight: 600, 
        fontFamily: 'monospace',
        color: getAmountColor(),
      }}>
        {formatCurrency(amount)}
      </div>
    </div>
  );
};

export const PaymentTrackingCard: React.FC<PaymentTrackingCardProps> = ({ 
  shipmentId,
  shipmentRef,
  className = '',
  onTransactionClick
}) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch payment summary for this shipment
  const { data: summary, isLoading, error } = useQuery<PaymentSummary>({
    queryKey: ['payment-summary', shipmentId],
    queryFn: () => transactionsApi.getSummary(shipmentId),
    enabled: !!shipmentId,
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Filter transactions by direction
  const getTransactionsByDirection = (direction: string): Transaction[] => {
    if (!summary?.transactions) return [];
    return summary.transactions.filter(t => t.direction === direction);
  };

  if (isLoading) {
    return (
      <CardContainer variant="secondary" className={className}>
        <SectionHeader 
          title="Payment Tracking"
          icon={<div style={{ width: '15px', height: '24px' }}><DollarIcon /></div>}
          showBottomBorder={true}
        />
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      </CardContainer>
    );
  }

  if (error || !summary) {
    return (
      <CardContainer variant="secondary" className={className}>
        <SectionHeader 
          title="Payment Tracking"
          icon={<div style={{ width: '15px', height: '24px' }}><DollarIcon /></div>}
          showBottomBorder={true}
        />
        <div style={{ padding: '24px' }}>
          <Empty 
            description="No payment data" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </CardContainer>
    );
  }

  const buyerTransactions = getTransactionsByDirection('BUYER_RECEIVABLE');
  const supplierTransactions = getTransactionsByDirection('SUPPLIER_PAYABLE');
  const commissionTransactions = getTransactionsByDirection('COMMISSION');

  // Table style for consistent column widths
  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '8px 4px',
    textAlign: 'right',
    fontWeight: 600,
    color: colors.text.secondary,
    borderBottom: `2px solid ${colors.border.primary || '#e0e0e0'}`,
    fontSize: '11px',
    textTransform: 'uppercase',
  };

  const cellStyle: React.CSSProperties = {
    padding: '10px 4px',
    textAlign: 'right',
    borderBottom: `1px solid ${colors.border.light || '#f0f0f0'}`,
  };

  const labelCellStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
    cursor: 'pointer',
  };

  return (
    <CardContainer variant="secondary" className={className}>
      {/* Header */}
      <SectionHeader 
        title="Payment Tracking"
        icon={<div style={{ width: '15px', height: '24px' }}><DollarIcon /></div>}
        showBottomBorder={true}
      />

      <div style={{ padding: '12px' }}>
        {/* Summary Table */}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: 'left', width: '40%' }}>Category</th>
              <th style={{ ...headerCellStyle, width: '20%' }}>Invoiced</th>
              <th style={{ ...headerCellStyle, width: '20%' }}>Paid</th>
              <th style={{ ...headerCellStyle, width: '20%' }}>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {/* Buyer Receivables */}
            <tr onClick={() => toggleSection('buyer')} style={{ cursor: 'pointer' }}>
              <td style={labelCellStyle}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {expandedSections.includes('buyer') ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
                  Buyer Receivables
                  {buyerTransactions.length > 0 && (
                    <Tag style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px', marginLeft: '4px' }}>
                      {buyerTransactions.length}
                    </Tag>
                  )}
                </span>
              </td>
              <td style={cellStyle}>{formatCurrency(summary.buyerInvoiced)}</td>
              <td style={{ ...cellStyle, color: colors.status.positive, fontWeight: 500 }}>
                {formatCurrency(summary.buyerPaid)}
              </td>
              <td style={{ ...cellStyle, color: summary.buyerOutstanding > 0 ? (colors.status.warning || '#faad14') : colors.text.secondary }}>
                {formatCurrency(summary.buyerOutstanding)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Buyer Transactions Expanded */}
        {expandedSections.includes('buyer') && buyerTransactions.length > 0 && (
          <div style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '8px' }}>
            {buyerTransactions.map(tx => (
              <TransactionRow 
                key={tx.id} 
                transaction={tx}
                onClick={onTransactionClick ? () => onTransactionClick(tx) : undefined}
              />
            ))}
          </div>
        )}

        {/* Supplier Payables */}
        <table style={tableStyle}>
          <tbody>
            <tr onClick={() => toggleSection('supplier')} style={{ cursor: 'pointer' }}>
              <td style={{ ...labelCellStyle, width: '40%' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {expandedSections.includes('supplier') ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
                  Supplier Payables
                  {supplierTransactions.length > 0 && (
                    <Tag style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px', marginLeft: '4px' }}>
                      {supplierTransactions.length}
                    </Tag>
                  )}
                </span>
              </td>
              <td style={{ ...cellStyle, width: '20%' }}>{formatCurrency(summary.supplierInvoiced)}</td>
              <td style={{ ...cellStyle, width: '20%', color: colors.status.negative, fontWeight: 500 }}>
                {formatCurrency(summary.supplierPaid)}
              </td>
              <td style={{ ...cellStyle, width: '20%', color: summary.supplierOutstanding > 0 ? (colors.status.warning || '#faad14') : colors.text.secondary }}>
                {formatCurrency(summary.supplierOutstanding)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Supplier Transactions Expanded */}
        {expandedSections.includes('supplier') && supplierTransactions.length > 0 && (
          <div style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '8px' }}>
            {supplierTransactions.map(tx => (
              <TransactionRow 
                key={tx.id} 
                transaction={tx}
                onClick={onTransactionClick ? () => onTransactionClick(tx) : undefined}
              />
            ))}
          </div>
        )}

        {/* Commission (if applicable) */}
        {(summary.commissionAmount > 0 || summary.commissionPaid > 0 || commissionTransactions.length > 0) && (
          <>
            <table style={tableStyle}>
              <tbody>
                <tr onClick={() => toggleSection('commission')} style={{ cursor: 'pointer' }}>
                  <td style={{ ...labelCellStyle, width: '40%' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      {expandedSections.includes('commission') ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
                      Commission
                      {commissionTransactions.length > 0 && (
                        <Tag style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px', marginLeft: '4px' }}>
                          {commissionTransactions.length}
                        </Tag>
                      )}
                    </span>
                  </td>
                  <td style={{ ...cellStyle, width: '20%' }}>{formatCurrency(summary.commissionAmount)}</td>
                  <td style={{ ...cellStyle, width: '20%', color: colors.status.positive, fontWeight: 500 }}>
                    {formatCurrency(summary.commissionPaid)}
                  </td>
                  <td style={{ ...cellStyle, width: '20%', color: (summary.commissionAmount - summary.commissionPaid) > 0 ? (colors.status.warning || '#faad14') : colors.text.secondary }}>
                    {formatCurrency(summary.commissionAmount - summary.commissionPaid)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Commission Transactions Expanded */}
            {expandedSections.includes('commission') && commissionTransactions.length > 0 && (
              <div style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '8px' }}>
                {commissionTransactions.map(tx => (
                  <TransactionRow 
                    key={tx.id} 
                    transaction={tx}
                    onClick={onTransactionClick ? () => onTransactionClick(tx) : undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Gross Profit */}
        <div style={{ 
          padding: '12px 4px',
          borderTop: `2px solid ${colors.border.primary || '#e0e0e0'}`,
          marginTop: '8px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontWeight: 700, fontSize: '14px' }}>Gross Profit</span>
            <span style={{ 
              fontWeight: 700, 
              fontSize: '16px',
              fontFamily: 'monospace',
              color: summary.grossProfit >= 0 ? colors.status.positive : colors.status.negative,
            }}>
              {formatCurrency(summary.grossProfit)}
            </span>
          </div>

          {/* Profit breakdown by party */}
          {summary.grossProfit > 0 && (
            <div style={{ 
              backgroundColor: '#f9f9f9', 
              borderRadius: '4px', 
              padding: '8px',
              fontSize: '12px'
            }}>
              {/* Rimtrade */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <span style={{ fontWeight: 500 }}>
                  Rimtrade ({Math.round((summary.commissionShare || 0.4) * 100)}%)
                </span>
                <span style={{ fontFamily: 'monospace', color: colors.status.positive }}>
                  {formatCurrency(summary.rimtrade?.netProfit || 0)}
                </span>
              </div>
              {/* Show breakdown if both margin and commission exist */}
              {summary.rimtrade?.netMargin > 0 && summary.rimtrade?.netCommission > 0 && (
                <div style={{ fontSize: '11px', color: colors.text.secondary, marginLeft: '12px', marginBottom: '4px' }}>
                  Margin: {formatCurrency(summary.rimtrade.netMargin)} + Commission: {formatCurrency(summary.rimtrade.netCommission)}
                </div>
              )}

              {/* Commwell */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
              }}>
                <span style={{ fontWeight: 500 }}>
                  Commwell ({Math.round((1 - (summary.commissionShare || 0.4)) * 100)}%)
                </span>
                <span style={{ fontFamily: 'monospace', color: colors.status.positive }}>
                  {formatCurrency(summary.commwell?.netProfit || 0)}
                </span>
              </div>
              {/* Show breakdown if both margin and commission exist */}
              {summary.commwell?.netMargin > 0 && summary.commwell?.netCommission > 0 && (
                <div style={{ fontSize: '11px', color: colors.text.secondary, marginLeft: '12px' }}>
                  Margin: {formatCurrency(summary.commwell.netMargin)} + Commission: {formatCurrency(summary.commwell.netCommission)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '12px',
          paddingTop: '8px',
          borderTop: `1px solid ${colors.border.light || '#f0f0f0'}`,
          marginTop: '8px',
          fontSize: '11px',
          color: colors.text.secondary,
        }}>
          <span
            onClick={() => setModalOpen(true)}
            style={{ cursor: 'pointer', textDecoration: 'underline', color: colors.tertiary || '#179DB5' }}
          >
            {summary.transactions?.length || 0} transactions
          </span>
          {summary.transactions?.some(t => t.legacyTable) && (
            <Tooltip title="Some transactions were migrated from the legacy system">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ExclamationCircleOutlined style={{ fontSize: 11 }} />
                Includes legacy data
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <TransactionDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transactions={summary.transactions || []}
        shipmentRef={shipmentRef || summary.shipmentRef}
      />
    </CardContainer>
  );
};

export default PaymentTrackingCard;
